import listingAdapter from '@/features/listing/requests/adapters/listing-adapter';
import {
  type ListingsCollection,
  type ListingWithUser,
} from '@/features/listing/types/listing-types';
import { type ListingsSearchResponse } from '@/features/listing/types/listings-search-response-types';
import userAdapter from '@/features/user/requests/adapters/user-adapter';
import getUsersIdVerifications from '@/features/user/requests/get-users-id-verifications';
import sendHttpError from '@/libs/logger/requests/send-http-error';

export default async function getListings(
  searchParams: string,
  currentCurrencyName: string,
  bearerToken: string | null,
): Promise<ListingsCollection> {
  const url = `${process.env.NEXT_PUBLIC_API_URL}/v1/search?${searchParams}`;
  const headers: HeadersInit = { Currency: currentCurrencyName };

  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  const listingsResponse = await fetch(
    url,
    {
      cache: 'no-store',
      headers,
    },
  );

  // TODO: make error handle!
  if (!listingsResponse.ok) {
    sendHttpError({
      baseName: 'Get listings for search',
      functionName: 'getListings',
      method: 'GET',
      requestHeaders: headers,
      statusCode: listingsResponse.status,
      url,
    });

    console.error(listingsResponse.status);
    console.error(await listingsResponse.json());

    throw new Error('Failed to fetch data');
  }

  const listingsResponseData = await listingsResponse.json() as ListingsSearchResponse;

  let listings: ListingWithUser[] = [];

  if (listingsResponseData.items.length > 0) {
    const listingsUsersIds = listingsResponseData.items.map((listingItem) => listingItem.user.id);

    const usersVerifications = await getUsersIdVerifications({ usersIds: listingsUsersIds });

    listings = listingsResponseData.items.map((listingItem) => {
      const listingWithUser: ListingWithUser = {
        ...listingAdapter(listingItem.listing),
        user: userAdapter(listingItem.user),
      };

      const userVerification = usersVerifications[listingItem.user.id];

      if (userVerification) {
        listingWithUser.user.verifications.id = userVerification;
      }

      return listingWithUser;
    });
  }

  return {
    averageMonthRent: listingsResponseData.avg_monthly_rent,
    count: listingsResponseData.count,
    items: listings,
  };
}
